import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  AudioLines,
  CheckCircle2,
  Clock3,
  History,
  Lightbulb,
  LoaderCircle,
  MessageSquare,
  Mic,
  Play,
  RotateCcw,
  Send,
  ShieldAlert,
  ShieldCheck,
  Square,
  Trophy,
  X,
  Trash2,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import CounselPracticeGrowthReport from '../components/counselPractice/CounselPracticeGrowthReport'
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import CounselPracticeProgress from '../components/counselPractice/CounselPracticeProgress'
import CounselPracticeSeasonManager from '../components/counselPractice/CounselPracticeSeasonManager'
import {
  buildMicrophoneCheckPayload,
  getMicrophoneSupport,
  inspectMicrophone,
  MICROPHONE_CHECK_STATUS,
} from '../lib/audio/microphoneDiagnostics'

const DEFAULT_INFORMATION_STATUSES = [
  { value: 'RANDOM', label: '무작위 선택' },
  { value: 'NONE', label: '정보 없음' },
  { value: 'CUSTOMER_MEMORY', label: '고객 기억만 있음' },
  { value: 'PARTIAL_DOCUMENT', label: '일부 자료 확인' },
  { value: 'VERIFIED', label: '보장 내역 확인' },
]

const EMPTY_CATALOG = {
  scenarios: [],
  missions: [],
  personas: [],
  difficulties: [],
  practiceModes: [],
  informationStatuses: DEFAULT_INFORMATION_STATUSES,
}

const MICROPHONE_STATUS_COPY = {
  [MICROPHONE_CHECK_STATUS.IDLE]: {
    title: '마이크 확인이 필요합니다.',
    detail: '마이크 확인을 누른 뒤 3초 동안 말씀해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.CHECKING]: {
    title: '마이크 소리를 확인하고 있습니다.',
    detail: '평소처럼 말씀해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.READY]: {
    title: '마이크가 준비됐습니다.',
    detail: '말소리가 정상적으로 들어옵니다.',
  },
  [MICROPHONE_CHECK_STATUS.UNSUPPORTED]: {
    title: '이 환경에서는 음성 답변을 사용할 수 없습니다.',
    detail: '지원되는 브라우저를 이용하거나 채팅으로 답변해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.INSECURE]: {
    title: '안전한 연결이 필요합니다.',
    detail: 'HTTPS로 접속한 뒤 다시 시도해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.DENIED]: {
    title: '마이크 사용 권한이 꺼져 있습니다.',
    detail: '브라우저 설정에서 마이크를 허용해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.NO_DEVICE]: {
    title: '연결된 마이크가 없습니다.',
    detail: '마이크를 연결한 뒤 다시 점검해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.UNAVAILABLE]: {
    title: '마이크를 지금 사용할 수 없습니다.',
    detail: '다른 프로그램에서 사용 중인지 확인해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.NO_SIGNAL]: {
    title: '말소리가 들리지 않습니다.',
    detail: '연결 상태와 입력 음량을 확인한 뒤 다시 점검해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.CHANGED]: {
    title: '마이크 연결이 바뀌었습니다.',
    detail: '계속하려면 마이크를 다시 확인해 주세요.',
  },
  [MICROPHONE_CHECK_STATUS.ERROR]: {
    title: '마이크를 확인하지 못했습니다.',
    detail: '잠시 후 다시 시도하거나 채팅으로 답변해 주세요.',
  },
}

function MicrophoneCheckPanel({ check, onCheck, disabled = false }) {
  const status = check?.status || MICROPHONE_CHECK_STATUS.IDLE
  const copy = MICROPHONE_STATUS_COPY[status] || MICROPHONE_STATUS_COPY[MICROPHONE_CHECK_STATUS.IDLE]
  const checking = status === MICROPHONE_CHECK_STATUS.CHECKING
  const ready = status === MICROPHONE_CHECK_STATUS.READY
  const needsAttention = ![
    MICROPHONE_CHECK_STATUS.IDLE,
    MICROPHONE_CHECK_STATUS.CHECKING,
    MICROPHONE_CHECK_STATUS.READY,
  ].includes(status)
  const detail = ready && check?.deviceLabel
    ? `${check.deviceLabel}에서 말소리가 확인됐습니다.`
    : copy.detail

  return (
    <div
      className={`flex items-start gap-3 rounded-md border px-3 py-3 ${
        ready
          ? 'border-emerald-200 bg-emerald-50'
          : needsAttention
            ? 'border-amber-200 bg-amber-50'
            : 'border-slate-200 bg-slate-50'
      }`}
      data-testid="counsel-mic-status"
      aria-live="polite"
    >
      {checking ? (
        <LoaderCircle className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-sky-600" aria-hidden="true" />
      ) : ready ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
      ) : needsAttention ? (
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
      ) : (
        <Mic className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-800">{copy.title}</div>
        <div className="mt-0.5 break-words text-xs leading-5 text-slate-600" title={ready ? check?.deviceLabel : undefined}>
          {detail}
        </div>
      </div>
      <button
        type="button"
        onClick={onCheck}
        disabled={disabled || checking}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="counsel-mic-check"
      >
        {checking ? '확인 중' : ready ? '다시 확인' : '마이크 확인'}
      </button>
    </div>
  )
}

function VoiceRetentionNotice() {
  return (
    <p className="text-xs leading-5 text-slate-500" data-testid="counsel-voice-retention">
      음성 원본은 저장하지 않습니다. 변환된 답변과 말하기 지표만 남습니다.
    </p>
  )
}

function CustomerInformationPanel({ information, compact = false }) {
  if (!information) return null
  const facts = Array.isArray(information.visibleFacts) ? information.visibleFacts : []
  const unknownItems = Array.isArray(information.unknownItems) ? information.unknownItems : []

  return (
    <section className="border-b border-slate-200 bg-sky-50/70 px-4 py-3 sm:px-6" data-testid="counsel-customer-information" aria-label="연습용 고객 정보">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-slate-900">{information.statusLabel || '고객 정보'}</span>
        <span className="text-xs font-medium text-sky-700">연습용 가상 정보</span>
        {information.profileTitle && <span className="text-xs text-slate-500">{information.profileTitle}</span>}
      </div>
      {information.overview && <p className="mt-1 text-sm leading-5 text-slate-700">{information.overview}</p>}
      {facts.length > 0 && (
        <dl className={`mt-2 grid gap-x-5 gap-y-1 text-sm ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2 2xl:grid-cols-3'}`}>
          {facts.map((fact, index) => (
            <div key={`${fact?.label || 'fact'}-${index}`} className="flex min-w-0 gap-2">
              <dt className="shrink-0 text-slate-500">{fact?.label}</dt>
              <dd className="min-w-0 font-medium text-slate-900">{fact?.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {!compact && unknownItems.length > 0 && (
        <p className="mt-2 text-xs leading-5 text-slate-500">
          추가 확인이 필요한 항목: {unknownItems.join(', ')}
        </p>
      )}
    </section>
  )
}

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function formatElapsed(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0)
  const minutes = String(Math.floor(safe / 60)).padStart(2, '0')
  const seconds = String(safe % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

function formatDateTime(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function createIdempotencyKey() {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return `turn-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}
function responseHintForCustomerTurn(turn, elapsedSeconds) {
  const text = String(turn?.text || '')
  const nextStep = elapsedSeconds >= 45
    ? '답변 끝에는 지금 할 수 있는 다음 행동 하나를 제안해 보세요.'
    : '고객이 이해하기 쉬운 순서로 한 가지씩 확인해 보세요.'
  if (/보험료|갱신|납입/.test(text)) return `보험료 부담을 먼저 인정하고, 현재 보험료와 갱신 기준을 확인해 보세요. ${nextStep}`
  if (/실손|실비|세대|전환/.test(text)) return `전환 여부를 단정하지 말고, 현재 계약의 가입 시기와 보장 내용을 함께 확인해 보세요. ${nextStep}`
  if (/증권|가입일|상품명|계약/.test(text)) return `가입일과 상품명처럼 바로 확인할 수 있는 정보부터 안내해 보세요. ${nextStep}`
  if (/치료|진단|입원|통원|비급여/.test(text)) return `최근 치료 경험을 먼저 듣고, 비교에 필요한 보장 조건을 차분히 확인해 보세요. ${nextStep}`
  if (/보장|청구|지급|얼마/.test(text)) return `지급 여부를 단정하지 말고, 확인이 필요한 기준을 먼저 설명해 보세요. ${nextStep}`
  return `고객이 방금 말한 걱정을 한 문장으로 받아 준 뒤, 확인할 정보 하나만 이어서 물어보세요. ${nextStep}`
}


function scoreTone(score) {
  const value = Number(score) || 0
  if (value >= 80) return 'bg-emerald-500'
  if (value >= 60) return 'bg-sky-500'
  if (value >= 40) return 'bg-amber-500'
  return 'bg-rose-500'
}

function SafetyBadge({ status, label }) {
  const safeStatus = status || 'PASS'
  if (safeStatus === 'FAIL') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-2.5 py-1 text-sm font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
        <ShieldAlert className="h-4 w-4" aria-hidden="true" />
        {label || '실패'}
      </span>
    )
  }
  if (safeStatus === 'WARN') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-sm font-semibold text-amber-800 ring-1 ring-inset ring-amber-200">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        {label || '주의'}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-sm font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
      <ShieldCheck className="h-4 w-4" aria-hidden="true" />
      {label || '통과'}
    </span>
  )
}

function SectionScores({ sections = [] }) {
  return (
    <div className="space-y-3" data-testid="counsel-section-scores">
      {sections.map((section) => {
        const applicable = section?.applicable !== false
        const value = applicable ? Math.max(0, Math.min(100, Number(section?.score) || 0)) : 0
        return (
          <div key={section.code || section.label} className="grid grid-cols-[5.5rem_1fr_3rem] items-center gap-3">
            <span className="text-sm font-medium text-slate-700">{section.label}</span>
            <div className="h-2.5 overflow-hidden rounded bg-slate-100" aria-hidden="true">
              {applicable && <div className={`h-full ${scoreTone(value)}`} style={{ width: `${value}%` }} />}
            </div>
            <span className={`text-right text-sm font-semibold ${applicable ? 'text-slate-800' : 'text-slate-400'}`}>
              {applicable ? Math.round(value) : 'N/A'}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function ScoreRadar({ sections = [] }) {
  const data = sections
    .filter((section) => section?.applicable !== false && Number.isFinite(Number(section?.score)))
    .map((section) => ({
      code: section.code,
      label: section.label,
      score: Math.max(0, Math.min(100, Number(section.score))),
    }))
  if (data.length < 3) return null

  const strongest = data.reduce((best, item) => item.score > best.score ? item : best, data[0])
  const weakest = data.reduce((lowest, item) => item.score < lowest.score ? item : lowest, data[0])

  return (
    <div data-testid="counsel-score-radar">
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1.5 font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
          강점 {strongest.label} {Math.round(strongest.score)}점
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-2.5 py-1.5 font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
          보완 {weakest.label} {Math.round(weakest.score)}점
        </span>
      </div>
      <div className="mt-3 h-[320px] w-full min-w-0" aria-label="상담 역량 항목별 점수 그래프">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="72%" margin={{ top: 18, right: 38, bottom: 18, left: 38 }}>
            <PolarGrid stroke="#cbd5e1" />
            <PolarAngleAxis dataKey="label" tick={{ fill: '#334155', fontSize: 11, fontWeight: 600 }} />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tickCount={6}
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false}
            />
            <Tooltip
              formatter={(value) => [`${Math.round(Number(value) || 0)}점`, '점수']}
              contentStyle={{ borderRadius: 6, borderColor: '#cbd5e1', fontSize: 12 }}
            />
            <Radar
              name="상담 역량"
              dataKey="score"
              stroke="#2563eb"
              strokeWidth={2.5}
              fill="#3b82f6"
              fillOpacity={0.35}
              dot={{ r: 3, fill: '#1d4ed8' }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function CriterionFeedback({ criteria = [] }) {
  const measured = criteria.filter((criterion) => criterion?.applicable !== false)
  if (!measured.length) return null

  return (
    <div className="divide-y divide-slate-200 border-y border-slate-200" data-testid="counsel-criterion-feedback">
      {measured.map((criterion) => {
        const status = criterion?.status || 'FAIL'
        const passed = status === 'PASS'
        const partial = status === 'PARTIAL'
        const StatusIcon = passed ? CheckCircle2 : AlertTriangle
        const statusLabel = passed ? '충족' : partial ? '보완' : '미흡'
        const statusClass = passed
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : partial
            ? 'bg-amber-50 text-amber-800 ring-amber-200'
            : 'bg-rose-50 text-rose-700 ring-rose-200'
        const iconClass = passed ? 'text-emerald-600' : partial ? 'text-amber-600' : 'text-rose-600'
        const hasEvidence = criterion?.evidenceMatched === true
          || (criterion?.evidenceMatched == null && Boolean(criterion?.evidence))
        const reason = criterion?.reason
          || (hasEvidence ? '해당 행동이 답변에서 확인되었습니다.' : '답변에서 해당 내용을 확인할 수 없었습니다.')
        const score = Number(criterion?.score)

        return (
          <div
            key={criterion.code || criterion.label}
            className="grid gap-3 py-4 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-5"
          >
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <StatusIcon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden="true" />
                <span>{criterion.label}</span>
              </div>
              <span className={`mt-2 inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusClass}`}>
                {statusLabel}{Number.isFinite(score) ? ` · ${Math.round(score)}점` : ''}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm leading-6 text-slate-700">{reason}</p>
              {hasEvidence && criterion.evidence && (
                <blockquote className="mt-2 border-l-2 border-slate-300 pl-3 text-sm leading-6 text-slate-600">
                  <span className="font-medium text-slate-700">내 답변 </span>
                  <span className="whitespace-pre-wrap break-words">“{criterion.evidence}”</span>
                </blockquote>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function Conversation({ turns = [] }) {
  const scrollRef = useRef(null)
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return undefined
    const frame = window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
    return () => window.cancelAnimationFrame(frame)
  }, [turns.length])

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6"
      data-testid="counsel-transcript"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {turns.map((turn) => {
          const planner = turn.speaker === 'PLANNER'
          return (
            <div key={turn.id || turn.turnNo} className={`flex ${planner ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[88%] sm:max-w-[76%] ${planner ? 'text-right' : 'text-left'}`}>
                <div className="mb-1 text-xs font-medium text-slate-500">{turn.speakerLabel || (planner ? '나' : '고객')}</div>
                <div
                  className={`rounded-lg px-4 py-3 text-left text-sm leading-6 ring-1 ring-inset ${
                    planner
                      ? 'bg-sky-600 text-white ring-sky-600'
                      : 'bg-white text-slate-800 ring-slate-200'
                  }`}
                >
                  {turn.text}
                  {turn.inputType === 'VOICE' && (
                    <span className={`ml-2 inline-flex align-middle ${planner ? 'text-sky-100' : 'text-slate-400'}`} title="음성으로 입력한 답변">
                      <Mic className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SpeechHabits({ habits }) {
  if (!habits) return null

  return (
    <section className="border-t border-slate-200 py-6" aria-labelledby="speech-habits-title" data-testid="counsel-speech-habits">
      <div className="flex items-center gap-2">
        <AudioLines className="h-5 w-5 text-sky-700" aria-hidden="true" />
        <h3 id="speech-habits-title" className="text-base font-semibold text-slate-950">말하기 습관</h3>
      </div>
      {habits.measured === false ? (
        <p className="mt-3 text-sm leading-6 text-slate-600">{habits.reason}</p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-5 border-y border-slate-200 py-4 sm:grid-cols-3 lg:grid-cols-6">
            <div>
              <div className="text-xs font-medium text-slate-500">말하기 속도</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{habits.paceLabel}</div>
              <div className="mt-0.5 text-xs text-slate-500">1분에 {habits.pacePerMinute}어절</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500">가장 긴 답변</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{habits.longestAnswerSeconds}초</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500">군더더기 표현</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{habits.fillerCount}회</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500">같은 말로 시작</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{habits.repeatedStartCount}회</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500">긴 멈춤</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{habits.pauseCount || 0}회</div>
              {Number(habits.longestPauseSeconds) > 0 && <div className="mt-0.5 text-xs text-slate-500">최장 {habits.longestPauseSeconds}초</div>}
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500">침묵 비율</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {habits.averageSilencePercent == null ? '측정 안 됨' : `${habits.averageSilencePercent}%`}
              </div>
            </div>
          </div>
          {habits.summary && <p className="mt-4 text-sm leading-6 text-slate-700">{habits.summary}</p>}
          {Array.isArray(habits.suggestions) && habits.suggestions.length > 0 && (
            <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
              {habits.suggestions.map((suggestion) => (
                <li key={suggestion} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden="true" />
                  <span>{suggestion}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}

function ResultPanel({ session, onRetry }) {
  const evaluation = session?.evaluation
  if (!evaluation) return null
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6" data-testid="counsel-result">
      <div className="mx-auto max-w-4xl">
        <div className="grid gap-4 border-b border-slate-200 pb-6 sm:grid-cols-[10rem_1fr] sm:items-center">
          <div className="flex h-36 flex-col items-center justify-center rounded-lg bg-slate-950 text-white">
            <Trophy className="h-5 w-5 text-amber-300" aria-hidden="true" />
            <div className="mt-2 text-4xl font-bold" data-testid="counsel-rank">{evaluation.rank}</div>
            <div className="mt-1 text-sm text-slate-300">{Math.round(Number(evaluation.totalScore) || 0)}점</div>
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold text-slate-950">연습 결과</h2>
              <SafetyBadge status={evaluation.safetyStatus} label={`준법 점검 ${evaluation.safetyStatusLabel}`} />
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-600">{session.contentTitle}</p>
            {evaluation.safetyReason && (
              <p className="mt-1 text-sm leading-6 text-slate-600">{evaluation.safetyReason}</p>
            )}
            {evaluation.scoreConfidence && (
              <div className="mt-3 border-l-2 border-teal-500 bg-teal-50 px-3 py-2 text-sm leading-5 text-slate-700" data-testid="counsel-score-confidence">
                <span className="mr-2 font-semibold text-teal-800">채점 신뢰도 {evaluation.scoreConfidence.levelLabel}</span>
                <span>{evaluation.scoreConfidence.message}</span>
                {evaluation.scoreConfidence.humanCalibration && (
                  <div className="mt-1 text-xs text-teal-900" data-testid="counsel-human-calibration">
                    {evaluation.scoreConfidence.humanCalibration.statusLabel}
                    {' · '}참고 {evaluation.scoreConfidence.humanCalibration.referenceScore}점
                    {' · '}사람 평가 {evaluation.scoreConfidence.humanCalibration.peerReviewCount}건
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              다시 연습
            </button>
          </div>
        </div>

        <section className="py-6" aria-labelledby="section-score-title">
          <h3 id="section-score-title" className="text-base font-semibold text-slate-950">상담 역량 분석</h3>
          <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(18rem,0.95fr)] lg:items-center">
            <ScoreRadar sections={evaluation.sections || []} />
            <div>
              <h4 className="text-sm font-semibold text-slate-800">항목별 점수</h4>
              <div className="mt-4">
                <SectionScores sections={evaluation.sections || []} />
              </div>
            </div>
          </div>
        </section>

        {Array.isArray(evaluation.criteria) && evaluation.criteria.some((criterion) => criterion?.applicable !== false) && (
          <section className="border-t border-slate-200 py-6" aria-labelledby="criterion-feedback-title">
            <h3 id="criterion-feedback-title" className="text-base font-semibold text-slate-950">답변 근거와 피드백</h3>
            <div className="mt-4">
              <CriterionFeedback criteria={evaluation.criteria} />
            </div>
          </section>
        )}

        <SpeechHabits habits={evaluation.speechHabits} />

        <div className="grid border-t border-slate-200 lg:grid-cols-2">
          <section className="py-6 lg:pr-6" aria-labelledby="strength-title">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
              <h3 id="strength-title" className="font-semibold">잘한 점</h3>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-700">{evaluation.strength}</p>
          </section>
          <section className="border-t border-slate-200 py-6 lg:border-l lg:border-t-0 lg:pl-6" aria-labelledby="priority-title">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              <h3 id="priority-title" className="font-semibold">먼저 보완할 점</h3>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-700">{evaluation.priority}</p>
          </section>
        </div>

        <section className="border-t border-slate-200 py-6" aria-labelledby="next-action-title">
          <h3 id="next-action-title" className="text-base font-semibold text-slate-950">다음에는 이렇게 해보세요</h3>
          <p className="mt-3 border-l-4 border-sky-500 bg-sky-50 px-4 py-3 text-sm font-medium leading-6 text-slate-800">
            {evaluation.nextAction}
          </p>
        </section>
      </div>
    </div>
  )
}

function ConversationReplay({ session }) {
  const turns = Array.isArray(session?.turns) ? session.turns : []
  return (
    <div className="h-full overflow-y-auto bg-slate-50 px-4 py-5 sm:px-6" data-testid="counsel-conversation-replay">
      <div className="mx-auto max-w-3xl">
        <div className="border-b border-slate-200 pb-4">
          <h3 className="font-semibold text-slate-950">{session?.contentTitle}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {session?.personaLabel} · {session?.difficultyLabel} · {session?.practiceModeLabel}
          </p>
        </div>
        <div className="mt-5 flex flex-col gap-4">
          {turns.map((turn) => {
            const planner = turn.speaker === 'PLANNER'
            return (
              <div key={turn.id || turn.turnNo} className={`flex ${planner ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[88%] sm:max-w-[76%] ${planner ? 'text-right' : 'text-left'}`}>
                  <div className="mb-1 text-xs font-medium text-slate-500">{turn.speakerLabel || (planner ? '나' : '고객')}</div>
                  <div className={`rounded-lg px-4 py-3 text-left text-sm leading-6 ring-1 ring-inset ${planner ? 'bg-sky-600 text-white ring-sky-600' : 'bg-white text-slate-800 ring-slate-200'}`}>
                    <span className="whitespace-pre-wrap break-words">{turn.text}</span>
                    {turn.inputType === 'VOICE' && <Mic className={`ml-2 inline h-3.5 w-3.5 ${planner ? 'text-sky-100' : 'text-slate-400'}`} aria-label="음성 입력" />}
                  </div>
                </div>
              </div>
            )
          })}
          {turns.length === 0 && <p className="py-16 text-center text-sm text-slate-500">저장된 대화가 없습니다.</p>}
        </div>
      </div>
    </div>
  )
}

function PracticeResultModal({ session, activeTab, onTabChange, onClose, onRetry }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  if (!session) return null
  const hasResult = Boolean(session.evaluation)

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/55 p-3 sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        className="flex h-[min(88dvh,900px)] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="counsel-result-modal-title"
        data-testid="counsel-result-modal"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 id="counsel-result-modal-title" className="truncate text-lg font-semibold text-slate-950">연습 기록</h2>
            <p className="mt-1 truncate text-sm text-slate-500">{session.contentTitle}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800" aria-label="연습 기록 닫기" title="닫기">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>
        <div className="flex shrink-0 gap-1 border-b border-slate-200 px-4 sm:px-6" role="tablist" aria-label="연습 기록 보기">
          <button type="button" role="tab" aria-selected={activeTab === 'RESULT'} disabled={!hasResult} onClick={() => onTabChange('RESULT')} className={`h-11 border-b-2 px-4 text-sm font-semibold ${activeTab === 'RESULT' ? 'border-sky-600 text-sky-700' : 'border-transparent text-slate-500 hover:text-slate-800'} disabled:cursor-not-allowed disabled:opacity-40`}>결과 보기</button>
          <button type="button" role="tab" aria-selected={activeTab === 'REPLAY'} onClick={() => onTabChange('REPLAY')} className={`h-11 border-b-2 px-4 text-sm font-semibold ${activeTab === 'REPLAY' ? 'border-sky-600 text-sky-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>대화 다시 보기</button>
        </div>
        <div className="min-h-0 flex-1">
          {activeTab === 'RESULT' && hasResult ? <ResultPanel session={session} onRetry={onRetry} /> : <ConversationReplay session={session} />}
        </div>
      </section>
    </div>
  )
}

export default function CounselPracticePage() {
  const [catalog, setCatalog] = useState(EMPTY_CATALOG)
  const [workspaceView, setWorkspaceView] = useState('PRACTICE')
  const [tab, setTab] = useState('FULL_SCENARIO')
  const [contentCode, setContentCode] = useState('')
  const [practiceMode, setPracticeMode] = useState('CHAT')
  const [sourceAnalysisKey, setSourceAnalysisKey] = useState(null)
  const [persona, setPersona] = useState('RANDOM')
  const [informationStatus, setInformationStatus] = useState('RANDOM')
  const [difficulty, setDifficulty] = useState('BEGINNER')
  const [session, setSession] = useState(null)
  const [resultModalSession, setResultModalSession] = useState(null)
  const [resultModalTab, setResultModalTab] = useState('RESULT')
  const [historyItems, setHistoryItems] = useState([])
  const [input, setInput] = useState('')
  const [inputType, setInputType] = useState('TEXT')
  const [voiceInputMeta, setVoiceInputMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [listening, setListening] = useState(false)
  const [responseDelaySeconds, setResponseDelaySeconds] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [microphoneCheck, setMicrophoneCheck] = useState({ status: MICROPHONE_CHECK_STATUS.IDLE })
  const recognitionRef = useRef(null)
  const microphoneCheckAbortRef = useRef(null)
  const actionLockRef = useRef(false)
  const recognitionStartedAtRef = useRef(null)
  const recognitionLastResultAtRef = useRef(null)
  const recognitionPauseCountRef = useRef(0)
  const recognitionPauseTotalMsRef = useRef(0)
  const recognitionLongestPauseMsRef = useRef(0)
  const responseDelayStartRef = useRef(null)
  const pendingTurnRef = useRef(null)

  const speechRecognition = useMemo(() => {
    if (typeof window === 'undefined') return null
    return window.SpeechRecognition || window.webkitSpeechRecognition || null
  }, [])

  const voiceSupport = useMemo(
    () => getMicrophoneSupport({ speechRecognition }),
    [speechRecognition],
  )
  const microphoneReady = microphoneCheck.status === MICROPHONE_CHECK_STATUS.READY
  const contentOptions = tab === 'QUICK_MISSION' ? catalog.missions : catalog.scenarios
  const selectedContent = contentOptions.find((item) => item.code === contentCode) || contentOptions[0] || null
  const active = session?.status === 'IN_PROGRESS'
  const evaluationFailed = active && session?.evaluationStatus === 'FAILED'
  const sessionTurns = session?.turns || []
  const lastTurn = sessionTurns[sessionTurns.length - 1] || null
  const waitingForPlannerReply = active && !busy && !listening && lastTurn?.speaker === 'CUSTOMER'
  const responseHint = waitingForPlannerReply && responseDelaySeconds >= 18 ? responseHintForCustomerTurn(lastTurn, responseDelaySeconds) : ''
  const evaluationProcessing = active && session?.evaluationStatus === 'PROCESSING'

  const loadHistory = async () => {
    try {
      const response = await api.get('/counsel-practice/sessions', { params: { limit: 30 }, __silent: true })
      const data = unwrap(response)
      setHistoryItems(Array.isArray(data?.items) ? data.items : [])
    } catch (e) {
      setHistoryItems([])
    }
  }

  const refreshSessionDetail = async (sessionId) => {
    if (!sessionId) return null
    try {
      const response = await api.get(`/counsel-practice/sessions/${sessionId}`, { __silent: true })
      const loaded = unwrap(response)
      if (loaded) setSession(loaded)
      return loaded
    } catch (e) {
      return null
    }
  }

  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await api.get('/counsel-practice/catalog', { __silent: true })
        const data = unwrap(response) || EMPTY_CATALOG
        if (!mounted) return
        setCatalog({ ...EMPTY_CATALOG, ...data })
        setContentCode(data?.scenarios?.[0]?.code || '')
        await loadHistory()
      } catch (e) {
        if (mounted) setError(getApiErrorMessage(e, '상담연습 목록을 불러오지 못했습니다.'))
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
      recognitionRef.current?.stop?.()
      microphoneCheckAbortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!active) return undefined
    const started = session?.startedAt ? new Date(session.startedAt).getTime() : Date.now()
    const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - started) / 1000)))
    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [active, session?.startedAt])
  useEffect(() => {
    if (!waitingForPlannerReply) {
      setResponseDelaySeconds(0)
      responseDelayStartRef.current = null
      return undefined
    }

    const turnKey = `${session?.id || ''}:${lastTurn?.id || lastTurn?.turnNo || ''}`
    const createdAt = new Date(lastTurn?.createdAt || '').getTime()
    if (responseDelayStartRef.current?.key !== turnKey) {
      responseDelayStartRef.current = { key: turnKey, startedAt: Number.isFinite(createdAt) ? createdAt : Date.now() }
    }
    const update = () => setResponseDelaySeconds(Math.max(0, Math.floor((Date.now() - responseDelayStartRef.current.startedAt) / 1000)))
    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [waitingForPlannerReply, session?.id, lastTurn?.id, lastTurn?.turnNo, lastTurn?.createdAt])


  useEffect(() => {
    const mediaDevices = typeof navigator === 'undefined' ? null : navigator.mediaDevices
    if (!mediaDevices?.addEventListener) return undefined

    const handleDeviceChange = () => {
      if (practiceMode !== 'VOICE_TURN_BASED') return
      microphoneCheckAbortRef.current?.abort()
      recognitionRef.current?.stop?.()
      setListening(false)
      setMicrophoneCheck({ status: MICROPHONE_CHECK_STATUS.CHANGED })
      if (active) setError('마이크 연결이 변경되었습니다. 다시 점검하거나 답변을 직접 입력해 주세요.')
    }

    mediaDevices.addEventListener('devicechange', handleDeviceChange)
    return () => mediaDevices.removeEventListener('devicechange', handleDeviceChange)
  }, [active, practiceMode])

  const acquireActionLock = () => {
    if (actionLockRef.current) return false
    actionLockRef.current = true
    setBusy(true)
    return true
  }

  const releaseActionLock = () => {
    actionLockRef.current = false
    setBusy(false)
  }

  const showPracticeRecord = (loaded, preferredTab = 'RESULT') => {
    if (!loaded) return
    setResultModalSession(loaded)
    setResultModalTab(loaded.evaluation && preferredTab === 'RESULT' ? 'RESULT' : 'REPLAY')
  }

  const closePracticeRecord = () => {
    setResultModalSession(null)
    setResultModalTab('RESULT')
    if (session?.status === 'COMPLETED') setSession(null)
  }

  const resetMicrophoneCheck = (status = MICROPHONE_CHECK_STATUS.IDLE) => {
    microphoneCheckAbortRef.current?.abort()
    microphoneCheckAbortRef.current = null
    setMicrophoneCheck({ status })
  }

  const recordMicrophoneDiagnostic = (check) => {
    const payload = buildMicrophoneCheckPayload({ check, speechRecognition })
    api.post('/counsel-practice/device-checks', payload, { __silent: true }).catch(() => {
      // Device telemetry must never block practice.
    })
  }

  const runMicrophoneCheck = async () => {
    if (!voiceSupport.supported) {
      const result = { status: voiceSupport.status, checkDurationMs: 0 }
      setMicrophoneCheck(result)
      recordMicrophoneDiagnostic(result)
      return
    }

    microphoneCheckAbortRef.current?.abort()
    const controller = new AbortController()
    microphoneCheckAbortRef.current = controller
    setMicrophoneCheck({ status: MICROPHONE_CHECK_STATUS.CHECKING })
    setError('')

    const result = await inspectMicrophone({ signal: controller.signal })
    if (!controller.signal.aborted && result.status !== MICROPHONE_CHECK_STATUS.CANCELLED) {
      setMicrophoneCheck(result)
      recordMicrophoneDiagnostic(result)
    }
    if (microphoneCheckAbortRef.current === controller) microphoneCheckAbortRef.current = null
  }

  const changePracticeMode = (nextMode) => {
    if (session || busy) return
    recognitionRef.current?.stop?.()
    setListening(false)
    setPracticeMode(nextMode)
    setError('')
    setInputType('TEXT')
    setVoiceInputMeta(null)
    resetMicrophoneCheck(
      nextMode === 'VOICE_TURN_BASED' && !voiceSupport.supported
        ? voiceSupport.status
        : MICROPHONE_CHECK_STATUS.IDLE,
    )
  }

  const switchTab = (nextTab) => {
    if (active || busy) return
    resetMicrophoneCheck()
    setTab(nextTab)
    setInputType('TEXT')
    setVoiceInputMeta(null)
    setSourceAnalysisKey(null)
    setContentCode(nextTab === 'QUICK_MISSION' ? catalog.missions?.[0]?.code || '' : catalog.scenarios?.[0]?.code || '')
    setSession(null)
    setError('')
  }

  const startSession = async () => {
    if (!selectedContent || busy || !acquireActionLock()) return
    if (practiceMode === 'VOICE_TURN_BASED' && !microphoneReady) {
      setError('음성으로 연습하려면 마이크를 먼저 확인해 주세요.')
      releaseActionLock()
      return
    }
    setError('')
    try {
      const response = await api.post('/counsel-practice/sessions', {
        contentType: tab,
        contentCode: selectedContent.code,
        practiceMode,
        persona,
        informationStatus,
        difficulty,
        sourceAnalysisKey: sourceAnalysisKey || undefined,
      }, { __silent: true })
      setSession(unwrap(response))
      setInput('')
      setInputType('TEXT')
      setVoiceInputMeta(null)
      setElapsed(0)
      pendingTurnRef.current = null
    } catch (e) {
      setError(getApiErrorMessage(e, '상담연습을 시작하지 못했습니다.'))
    } finally {
      releaseActionLock()
    }
  }

  const submitTurn = async () => {
    const text = input.trim()
    if (!text || !session?.id || busy || !acquireActionLock()) return
    const speechMetadata = inputType === 'VOICE' ? voiceInputMeta : null
    const signature = `${inputType}\n${text}\n${speechMetadata?.speechDurationMs || ''}\n${speechMetadata?.speechConfidence ?? ''}`
    if (pendingTurnRef.current?.signature !== signature) {
      pendingTurnRef.current = {
        signature,
        requestKey: createIdempotencyKey(),
      }
    }
    const requestKey = pendingTurnRef.current.requestKey
    setError('')
    try {
      const response = await api.post(`/counsel-practice/sessions/${session.id}/turns`, {
        text,
        inputType,
        ...(speechMetadata || {}),
      }, {
        __silent: true,
        headers: { 'Idempotency-Key': requestKey },
      })
      const updated = unwrap(response)
      setSession(updated)
      if (updated?.status === 'COMPLETED') showPracticeRecord(updated)
      setInput('')
      setInputType('TEXT')
      setVoiceInputMeta(null)
      pendingTurnRef.current = null
      if (updated?.status === 'COMPLETED') await loadHistory()
    } catch (e) {
      setError(getApiErrorMessage(e, '답변을 전송하지 못했습니다.'))
      await refreshSessionDetail(session.id)
    } finally {
      releaseActionLock()
    }
  }

  const completeSession = async () => {
    if (!session?.id || busy || !acquireActionLock()) return
    setError('')
    try {
      const response = await api.post(`/counsel-practice/sessions/${session.id}/complete`, {}, { __silent: true })
      const updated = unwrap(response)
      setSession(updated)
      showPracticeRecord(updated)
      await loadHistory()
    } catch (e) {
      setError(getApiErrorMessage(e, '연습 결과를 확인하지 못했습니다.'))
      await refreshSessionDetail(session.id)
    } finally {
      releaseActionLock()
    }
  }

  const retryEvaluation = async () => {
    if (!session?.id || busy || !acquireActionLock()) return
    setError('')
    try {
      const response = await api.post(
        `/counsel-practice/sessions/${session.id}/evaluation/retry`,
        {},
        { __silent: true },
      )
      const updated = unwrap(response)
      setSession(updated)
      if (updated?.status === 'COMPLETED') {
        showPracticeRecord(updated)
        await loadHistory()
      }
    } catch (e) {
      setError(getApiErrorMessage(e, '결과 분석을 다시 시작하지 못했습니다.'))
      await refreshSessionDetail(session.id)
    } finally {
      releaseActionLock()
    }
  }

  const openHistory = async (item) => {
    if (!item?.id || active || busy || !acquireActionLock()) return
    setError('')
    try {
      const response = await api.get(`/counsel-practice/sessions/${item.id}`, { __silent: true })
      const loaded = unwrap(response)
      setTab(loaded?.contentType || 'FULL_SCENARIO')
      setSourceAnalysisKey(null)
      setContentCode(loaded?.contentCode || '')
      setPracticeMode(loaded?.practiceMode || 'CHAT')
      setDifficulty(loaded?.difficulty || 'BEGINNER')
      resetMicrophoneCheck()
      setPersona(loaded?.persona || 'RANDOM')
      setInformationStatus(loaded?.customerInformation?.status || 'RANDOM')
      setInput('')
      setInputType('TEXT')
      setVoiceInputMeta(null)
      showPracticeRecord(loaded)
    } catch (e) {
      setError(getApiErrorMessage(e, '연습 기록을 불러오지 못했습니다.'))
    } finally {
      releaseActionLock()
    }
  }

  const deleteHistory = async (item) => {
    if (!item?.id || active || busy) return
    const confirmed = window.confirm(`${item.contentTitle} 연습 기록을 삭제할까요?`)
    if (!confirmed || !acquireActionLock()) return
    setError('')
    try {
      await api.delete(`/counsel-practice/sessions/${item.id}`, { __silent: true })
      setHistoryItems((current) => current.filter((historyItem) => historyItem.id !== item.id))
      if (resultModalSession?.id === item.id) {
        setResultModalSession(null)
        setResultModalTab('RESULT')
      }
      if (session?.id === item.id) {
        recognitionRef.current?.stop?.()
        setListening(false)
        resetMicrophoneCheck()
        setSession(null)
        setInput('')
        setInputType('TEXT')
        setVoiceInputMeta(null)
        pendingTurnRef.current = null
      }
    } catch (e) {
      setError(getApiErrorMessage(e, '연습 기록을 삭제하지 못했습니다.'))
    } finally {
      releaseActionLock()
    }
  }

  const resetPractice = () => {
    recognitionRef.current?.stop?.()
    setListening(false)
    resetMicrophoneCheck()
    setSession(null)
    setInput('')
    setInputType('TEXT')
    setVoiceInputMeta(null)
    setError('')
    pendingTurnRef.current = null
    setResultModalSession(null)
    setResultModalTab('RESULT')
    setSourceAnalysisKey(null)
  }

  const openRecommendedContent = (contentType, contentCode, recommendedPersona, recommendedDifficulty, analysisKey) => {
    if (active || busy || !contentCode) return
    resetPractice()
    setWorkspaceView('PRACTICE')
    setTab(contentType === 'FULL_SCENARIO' ? 'FULL_SCENARIO' : 'QUICK_MISSION')
    setContentCode(contentCode)
    setPracticeMode('CHAT')
    setPersona(recommendedPersona || 'RANDOM')
    setInformationStatus('RANDOM')
    setDifficulty(recommendedDifficulty || 'PRACTICAL')
    setSourceAnalysisKey(analysisKey || null)
  }

  const toggleSpeech = () => {
    if (!speechRecognition || busy || !active || !microphoneReady) {
      if (!microphoneReady) setError('마이크를 먼저 확인한 뒤 음성으로 답변해 주세요.')
      return
    }
    if (listening) {
      recognitionRef.current?.stop?.()
      return
    }
    const recognition = new speechRecognition()
    recognition.lang = 'ko-KR'
    recognition.continuous = false
    recognition.interimResults = true
    recognition.onstart = () => {
      const now = Date.now()
      recognitionStartedAtRef.current = now
      recognitionLastResultAtRef.current = now
      recognitionPauseCountRef.current = 0
      recognitionPauseTotalMsRef.current = 0
      recognitionLongestPauseMsRef.current = 0
      setVoiceInputMeta(null)
      setListening(true)
    }
    recognition.onresult = (event) => {
      const now = Date.now()
      const resultGapMs = now - (recognitionLastResultAtRef.current || now)
      if (resultGapMs >= 1200) {
        recognitionPauseCountRef.current += 1
        recognitionPauseTotalMsRef.current += resultGapMs
        recognitionLongestPauseMsRef.current = Math.max(recognitionLongestPauseMsRef.current, resultGapMs)
      }
      recognitionLastResultAtRef.current = now
      const alternatives = Array.from(event.results || [])
        .map((result) => result?.[0])
        .filter(Boolean)
      const transcript = alternatives
        .map((alternative) => alternative.transcript || '')
        .join(' ')
        .trim()
      if (transcript) {
        const elapsedMs = now - (recognitionStartedAtRef.current || now)
        const confidenceValues = alternatives
          .map((alternative) => Number(alternative.confidence))
          .filter(Number.isFinite)
        const speechMetadata = {
          speechDurationMs: Math.min(120000, Math.max(100, elapsedMs)),
          speechPauseCount: recognitionPauseCountRef.current,
          speechLongestPauseMs: Math.min(120000, recognitionLongestPauseMsRef.current),
          speechSilenceRatio: Math.min(0.95, recognitionPauseTotalMsRef.current / Math.max(100, elapsedMs)),
        }
        if (confidenceValues.length > 0) {
          speechMetadata.speechConfidence = confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length
        }
        setInput(transcript)
        setInputType('VOICE')
        setVoiceInputMeta(speechMetadata)
      }
    }
    recognition.onerror = (event) => {
      recognitionStartedAtRef.current = null
      recognitionLastResultAtRef.current = null
      if (['not-allowed', 'service-not-allowed'].includes(event?.error)) {
        setMicrophoneCheck({ status: MICROPHONE_CHECK_STATUS.DENIED })
        setError('마이크 사용 권한이 꺼져 있습니다. 권한을 허용한 뒤 다시 확인해 주세요.')
      } else if (event?.error === 'audio-capture') {
        setMicrophoneCheck({ status: MICROPHONE_CHECK_STATUS.NO_DEVICE })
        setError('연결된 마이크가 없습니다. 마이크를 연결한 뒤 다시 확인해 주세요.')
      } else {
        setError('말씀하신 내용을 인식하지 못했습니다. 다시 시도해 주세요.')
      }
    }
    recognition.onend = () => {
      recognitionStartedAtRef.current = null
      recognitionLastResultAtRef.current = null
      setListening(false)
    }
    recognitionRef.current = recognition
    try {
      recognition.start()
    } catch (e) {
      recognitionStartedAtRef.current = null
      recognitionLastResultAtRef.current = null
      setVoiceInputMeta(null)
      setListening(false)
      setError('음성 답변을 시작하지 못했습니다. 마이크를 다시 확인해 주세요.')
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[24rem] items-center justify-center text-slate-500">
        <LoaderCircle className="h-6 w-6 animate-spin" aria-label="불러오는 중" />
      </div>
    )
  }

  return (
    <main className="mx-auto w-full max-w-[1500px] px-3 py-4 sm:px-5 sm:py-6">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-medium text-sky-700">업무지원</p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-950">상담 시뮬레이션</h1>
        </div>
      </header>

      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist" aria-label="상담 시뮬레이션 화면">
        {[
          ['PRACTICE', '연습하기'],
          ['SEASON', '시즌·랭킹'],
          ['GROWTH', '나의 성장'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={workspaceView === value}
            onClick={() => setWorkspaceView(value)}
            disabled={busy || (active && value !== 'PRACTICE')}
            className={`h-10 shrink-0 border-b-2 px-4 text-sm font-semibold ${
              workspaceView === value
                ? 'border-slate-900 text-slate-950'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            } disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {label}
          </button>
        ))}
      </div>

      {workspaceView === 'PRACTICE' ? (
        <>
      <div className="mt-3 flex gap-1 border-b border-slate-200" role="tablist" aria-label="상담연습 유형">
        {[
          ['FULL_SCENARIO', '5분 상담'],
          ['QUICK_MISSION', '1분 미션'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => switchTab(value)}
            disabled={active}
            className={`h-10 border-b-2 px-4 text-sm font-semibold ${
              tab === value ? 'border-sky-600 text-sky-700' : 'border-transparent text-slate-500 hover:text-slate-800'
            } disabled:cursor-not-allowed`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="mt-5 grid min-h-[640px] gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(20rem,2fr)]">
        {!active && (
        <section className="min-w-0 border border-slate-200 bg-white p-5 sm:p-6" aria-label="연습 설정" data-testid="counsel-practice-settings">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-sky-700" aria-hidden="true" />
            <h2 className="font-semibold text-slate-950">연습 설정</h2>
          </div>

          <div className="mt-5 space-y-5">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">{tab === 'QUICK_MISSION' ? '미션' : '시나리오'}</span>
              <select
                value={contentCode}
                onChange={(event) => {
                  setContentCode(event.target.value)
                  setSourceAnalysisKey(null)
                }}
                disabled={Boolean(session)}
                className="mt-2 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 disabled:bg-slate-100"
                aria-label={tab === 'QUICK_MISSION' ? '미션 선택' : '시나리오 선택'}
              >
                {contentOptions.map((item) => <option key={item.code} value={item.code}>{item.title}</option>)}
              </select>
            </label>

            {selectedContent && !session && (
              <div className="border-l-2 border-slate-300 pl-3 text-sm leading-6 text-slate-600">
                <div className="font-medium text-slate-800">{selectedContent.insuranceTypeLabel}</div>
                <div>{selectedContent.consultationStageLabel}</div>
              </div>
            )}

            <fieldset disabled={Boolean(session)}>
              <legend className="text-sm font-medium text-slate-700">연습 방식</legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(catalog.practiceModes || []).map((mode) => {
                  const voiceUnavailable = mode.value === 'VOICE_TURN_BASED' && !voiceSupport.supported
                  const unavailableTitle = MICROPHONE_STATUS_COPY[voiceSupport.status]?.title
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => changePracticeMode(mode.value)}
                      disabled={Boolean(session)}
                      title={voiceUnavailable ? unavailableTitle : undefined}
                      className={`h-10 rounded-md border text-sm font-semibold ${
                        practiceMode === mode.value
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                      } disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      {mode.label}
                    </button>
                  )
                })}
              </div>
            </fieldset>

            {practiceMode === 'VOICE_TURN_BASED' && !session && (
              <div className="space-y-2">
                <MicrophoneCheckPanel
                  check={microphoneCheck}
                  onCheck={runMicrophoneCheck}
                  disabled={busy}
                />
                <VoiceRetentionNotice />
              </div>
            )}

            <label className="block">
              <span className="text-sm font-medium text-slate-700">고객 유형</span>
              <select
                value={persona}
                onChange={(event) => {
                  setPersona(event.target.value)
                  setSourceAnalysisKey(null)
                }}
                disabled={Boolean(session)}
                className="mt-2 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 disabled:bg-slate-100"
              >
                {(catalog.personas || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>

            <label className="block" data-testid="counsel-information-status-field">
              <span className="text-sm font-medium text-slate-700">고객 정보 상태</span>
              <select
                value={informationStatus}
                onChange={(event) => {
                  setInformationStatus(event.target.value)
                  setSourceAnalysisKey(null)
                }}
                disabled={Boolean(session)}
                className="mt-2 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 disabled:bg-slate-100"
                aria-label="고객 정보 상태 선택"
              >
                {(catalog.informationStatuses || DEFAULT_INFORMATION_STATUSES).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>

            <fieldset disabled={Boolean(session)}>
              <legend className="text-sm font-medium text-slate-700">난이도</legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(catalog.difficulties || []).map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      setDifficulty(item.value)
                      setSourceAnalysisKey(null)
                    }}
                    className={`h-10 rounded-md border text-sm font-semibold ${
                      difficulty === item.value
                        ? 'border-slate-800 bg-slate-900 text-white'
                        : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </fieldset>

            {!session ? (
              <button
                type="button"
                onClick={startSession}
                disabled={!selectedContent || busy || (practiceMode === 'VOICE_TURN_BASED' && !microphoneReady)}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="counsel-start"
              >
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
                연습 시작
              </button>
            ) : (
              <div className="border-t border-slate-200 pt-4 text-sm text-slate-600">
                <div className="font-semibold text-slate-900">{session.contentTitle}</div>
                <dl className="mt-3 grid grid-cols-[5rem_1fr] gap-y-2">
                  <dt>고객</dt><dd className="font-medium text-slate-800">{session.personaLabel}</dd>
                  <dt>난이도</dt><dd className="font-medium text-slate-800">{session.difficultyLabel}</dd>
                  <dt>방식</dt><dd className="font-medium text-slate-800">{session.practiceModeLabel}</dd>
                </dl>
              </div>
            )}
          </div>
        </section>
        )}

        {active && (
        <section className="flex h-[640px] min-h-[30rem] min-w-0 max-h-[calc(100dvh-6rem)] self-start flex-col overflow-hidden border border-slate-200 bg-slate-50" aria-label="상담 진행">
          {active && (
            <>
              <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900">{session.contentTitle}</div>
                  <div className="mt-0.5 text-xs text-slate-500">{session.personaLabel} · {session.difficultyLabel}</div>
                </div>
                <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700" aria-label={`연습 시간 ${formatElapsed(elapsed)}`}>
                    <Clock3 className="h-4 w-4" aria-hidden="true" />
                    {formatElapsed(elapsed)}
                  </span>
                  {busy && <LoaderCircle className="h-5 w-5 animate-spin text-sky-600" aria-label="고객이 답변을 준비하고 있습니다" />}
                  <button
                    type="button"
                    onClick={completeSession}
                    disabled={busy || evaluationFailed || evaluationProcessing || !(session?.turns || []).some((turn) => turn.speaker === 'PLANNER')}
                    className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="counsel-complete"
                    title="현재까지의 대화를 채점하고 종료"
                  >
                    <Square className="h-4 w-4" aria-hidden="true" />
                    여기서 마치고 채점
                  </button>
                </div>
              </div>
              <CustomerInformationPanel information={session.customerInformation} />
              <CounselPracticeProgress progress={session.conversationProgress} />
              <Conversation turns={session.turns || []} />
              {evaluationFailed && (
                <div className="border-t border-amber-200 bg-amber-50 px-4 py-5 sm:px-6" data-testid="counsel-evaluation-failed">
                  <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
                      <div>
                        <div className="font-semibold text-slate-900">결과 분석을 마치지 못했습니다.</div>
                        {session.evaluationErrorMessage && (
                          <p className="mt-1 text-sm leading-6 text-slate-600">{session.evaluationErrorMessage}</p>
                        )}
                        <p className="mt-1 text-sm leading-6 text-slate-600">대화 내용은 그대로 남아 있습니다. 결과 분석만 다시 시도해 주세요.</p>
                      </div>
                    </div>
                    <button type="button" onClick={retryEvaluation} disabled={busy} className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-md bg-amber-700 px-4 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-50">
                      {busy ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
                      결과 다시 분석
                    </button>
                  </div>
                </div>
              )}
              {evaluationProcessing && (
                <div className="border-t border-sky-200 bg-sky-50 px-4 py-5 sm:px-6" data-testid="counsel-evaluation-processing">
                  <div className="mx-auto flex max-w-3xl items-center gap-3 text-sm font-medium text-sky-800">
                    <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
                    결과를 분석하고 있습니다.
                  </div>
                </div>
              )}
              {!evaluationFailed && !evaluationProcessing && (
              <div className="border-t border-slate-200 bg-white p-3 sm:p-4">
                {practiceMode === 'VOICE_TURN_BASED' && !microphoneReady && (
                  <div className="mx-auto mb-3 max-w-3xl">
                    <MicrophoneCheckPanel
                      check={microphoneCheck}
                      onCheck={runMicrophoneCheck}
                      disabled={busy}
                    />
                  </div>
                )}
                {responseHint && (
                  <div className="mx-auto mb-3 flex max-w-3xl items-start gap-2 border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm leading-5 text-amber-950" data-testid="counsel-response-hint" role="status" aria-live="polite">
                    <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
                    <p>{responseHint}</p>
                  </div>
                )}
                <div className="mx-auto flex max-w-3xl items-end gap-2">
                  {practiceMode === 'VOICE_TURN_BASED' && (
                    <button
                      type="button"
                      onClick={toggleSpeech}
                      disabled={!speechRecognition || busy || !microphoneReady}
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md border ${
                        listening
                          ? 'border-rose-500 bg-rose-50 text-rose-700'
                          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                      } disabled:cursor-not-allowed disabled:opacity-40`}
                      title={listening ? '말하기 중지' : microphoneReady ? '마이크로 답변하기' : '마이크 확인 필요'}
                      aria-label={listening ? '말하기 중지' : microphoneReady ? '마이크로 답변하기' : '마이크 확인 필요'}
                    >
                      {listening ? <Square className="h-4 w-4" aria-hidden="true" /> : <Mic className="h-5 w-5" aria-hidden="true" />}
                    </button>
                  )}
                  <textarea
                    value={input}
                    onChange={(event) => {
                      setInput(event.target.value)
                      setInputType('TEXT')
                      setVoiceInputMeta(null)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault()
                        submitTurn()
                      }
                    }}
                    rows={2}
                    maxLength={2000}
                    disabled={busy || listening}
                    placeholder={listening ? '듣고 있습니다.' : '고객에게 할 말을 입력해 주세요.'}
                    className="min-h-11 flex-1 resize-none rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm leading-5 text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-100"
                    aria-label="상담 답변"
                  />
                  <button
                    type="button"
                    onClick={submitTurn}
                    disabled={!input.trim() || busy || listening}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
                    title="답변 보내기"
                    aria-label="답변 보내기"
                    data-testid="counsel-send"
                  >
                    <Send className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                {practiceMode === 'VOICE_TURN_BASED' && (
                  <div className="mx-auto mt-2 max-w-3xl"><VoiceRetentionNotice /></div>
                )}
              </div>
              )}
            </>
          )}
        </section>
        )}

        <aside className="flex h-[640px] min-h-[30rem] max-h-[calc(100dvh-6rem)] flex-col overflow-hidden border border-slate-200 bg-white" aria-label="최근 연습 기록">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-slate-500" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-slate-900">최근 연습</h2>
            </div>
            <button type="button" onClick={loadHistory} className="text-xs font-semibold text-sky-700 hover:text-sky-800">새로고침</button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {historyItems.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-500">아직 연습 기록이 없습니다.</div>}
            {historyItems.map((item) => (
              <div key={item.id} className="flex border-b border-slate-100 hover:bg-slate-50">
                <button
                  type="button"
                  onClick={() => openHistory(item)}
                  disabled={active || busy}
                  className="min-w-0 flex-1 px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="line-clamp-2 text-sm font-medium leading-5 text-slate-800">{item.contentTitle}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span>{formatDateTime(item.createdAt)}</span>
                    {item.status === 'COMPLETED' ? (
                      <span className="shrink-0 font-semibold text-slate-800">{item.rank} · {Math.round(Number(item.totalScore) || 0)}점</span>
                    ) : (
                      <span className="shrink-0">진행 중</span>
                    )}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => deleteHistory(item)}
                  disabled={active || busy}
                  className="m-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
                  title="기록 삭제"
                  aria-label={`${item.contentTitle} 기록 삭제`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </aside>
      </div>
      {resultModalSession && (
        <PracticeResultModal
          session={resultModalSession}
          activeTab={resultModalTab}
          onTabChange={setResultModalTab}
          onClose={closePracticeRecord}
          onRetry={resetPractice}
        />
      )}
        </>
      ) : workspaceView === 'SEASON' ? (
        <CounselPracticeSeasonManager />
      ) : (
        <CounselPracticeGrowthReport onPracticeContent={openRecommendedContent} />
      )}
    </main>
  )
}
